import React, { useState } from "react";
import { Plus, X, Search, AlertCircle, ArrowLeftRight, MoreVertical, Link as LinkIcon, Bookmark, Printer, Trash2 } from "lucide-react";
import Button from "../components/Button";
import { schools, getSchoolBySlug } from "../data/schools";
import { formatRupiah } from "../lib/format";
import { useCompare } from "../context/CompareContext";
import { Link } from "react-router-dom";
import logo from "../assets/images/logo-fix.png";
import SEO from "../components/SEO";

const CATEGORIES = [
  {
    title: "Match",
    rows: [
      {
        label: "Match Score",
        render: (s) => (
          <span className="font-extrabold text-purple text-xl">
            {s.matchScore ? `${s.matchScore}%` : "-"}
          </span>
        ),
      },
    ],
  },
  {
    title: "Biaya",
    rows: [
      {
        label: "SPP Bulanan",
        render: (s) => <span className="font-bold">{formatRupiah(s.monthlyTuition)}</span>,
      },
      {
        label: "Uang Pangkal",
        render: (s) => formatRupiah(s.entranceFee),
      },
    ],
  },
  {
    title: "Akademik",
    rows: [
      { label: "Jenjang", render: (s) => s.educationLevels.join(", ") },
      {
        label: "Kurikulum",
        render: (s) => <span className="font-bold text-ink">{s.curriculum}</span>,
      },
      { label: "Akreditasi", render: (s) => s.accreditation },
      { label: "Rasio Guru:Siswa", render: (s) => `1:${s.studentTeacherRatio}` },
    ],
  },
  {
    title: "Program & Aktivitas",
    rows: [
      { label: "Kebutuhan Khusus", render: (s) => (s.specialNeedsSupport ? "Tersedia" : "-") },
      {
        label: "Program Keagamaan",
        render: (s) => (s.religiousPrograms.length > 0 ? s.religiousPrograms.join(", ") : "-"),
      },
      { label: "Fasilitas Utama", render: (s) => s.facilities.join(", ") },
      { label: "Ekstrakurikuler", render: (s) => s.extracurriculars.join(", ") },
    ],
  },
];

export default function Compare() {
  const { slugs, removeCompare, clearCompare, toggleCompare, max, count } = useCompare();
  const [picking, setPicking] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const selected = slugs.map(getSchoolBySlug).filter(Boolean);
  const available = schools.filter((s) => !slugs.includes(s.slug));

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 print:p-8 print:m-0 print:max-w-none">
      <SEO
        title="Bandingkan Sekolah | MySchoolMate"
        description="Bandingkan beberapa sekolah berdasarkan biaya, kurikulum, fasilitas, lokasi, dan informasi penting lainnya."
        canonicalPath="/compare"
      />
      {/* Print-only Header */}
      <div className="hidden print:flex items-center justify-between mb-8 pb-4 border-b-2 border-ink">
        <img src={logo} alt="MySchoolMate Logo" className="h-10 w-auto" />
        <h2 className="text-xl font-extrabold text-ink">Laporan Perbandingan Sekolah</h2>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-8 print:hidden">
        <div className="max-w-2xl">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
            Bandingkan Sekolah
          </h1>
          <p className="mt-3 text-ink/70 text-lg">
            Pilih hingga {max} sekolah dan bandingkan fitur, biaya, serta kecocokannya.
          </p>
          {selected.length > 1 && (
            <div className="mt-5 flex sm:hidden items-center gap-2 text-ink/70 bg-cream w-fit px-4 py-2 rounded-full text-xs font-bold animate-pulse print:hidden">
              <ArrowLeftRight size={16} /> Geser tabel ke samping
            </div>
          )}
        </div>
        {count > 0 && (
          <div className="flex items-center print:hidden">
            {/* Desktop Actions */}
            <div className="hidden sm:flex items-center gap-2">
              <button
                className="inline-flex items-center gap-1.5 text-sm font-bold text-ink hover:bg-ink/5 px-3 py-2 rounded-lg transition-colors"
                onClick={() => alert("Fitur salin tautan belum tersedia")}
              >
                <LinkIcon size={16} /> Salin
              </button>
              <button
                className="inline-flex items-center gap-1.5 text-sm font-bold text-ink hover:bg-ink/5 px-3 py-2 rounded-lg transition-colors"
                onClick={() => alert("Fitur simpan belum tersedia")}
              >
                <Bookmark size={16} /> Simpan
              </button>
              <button
                className="inline-flex items-center gap-1.5 text-sm font-bold text-ink hover:bg-ink/5 px-3 py-2 rounded-lg transition-colors"
                onClick={() => window.print()}
              >
                <Printer size={16} /> Cetak
              </button>
              <div className="w-px h-6 bg-ink/20 mx-1"></div>
              <button
                onClick={clearCompare}
                data-testid="compare-clear"
                className="inline-flex items-center gap-1.5 text-sm font-bold text-coral hover:bg-coral/10 px-3 py-2 rounded-lg transition-colors"
              >
                <Trash2 size={16} /> Kosongkan
              </button>
            </div>

            {/* Mobile Dropdown */}
            <div className="sm:hidden relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="p-2 rounded-lg text-ink hover:bg-ink/5 transition-colors"
                aria-label="Aksi lainnya"
              >
                <MoreVertical size={24} />
              </button>
              
              {showDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)}></div>
                  <div className="absolute right-0 top-full mt-2 bg-white border border-ink/10 rounded-xl shadow-lg flex flex-col w-48 z-50 overflow-hidden py-1">
                    <button
                      className="inline-flex items-center gap-3 text-sm font-bold text-ink hover:bg-ink/5 px-4 py-3 text-left"
                      onClick={() => { setShowDropdown(false); alert("Fitur salin tautan belum tersedia"); }}
                    >
                      <LinkIcon size={16} /> Salin Tautan
                    </button>
                    <button
                      className="inline-flex items-center gap-3 text-sm font-bold text-ink hover:bg-ink/5 px-4 py-3 text-left"
                      onClick={() => { setShowDropdown(false); alert("Fitur simpan belum tersedia"); }}
                    >
                      <Bookmark size={16} /> Simpan
                    </button>
                    <button
                      className="inline-flex items-center gap-3 text-sm font-bold text-ink hover:bg-ink/5 px-4 py-3 text-left"
                      onClick={() => { setShowDropdown(false); window.print(); }}
                    >
                      <Printer size={16} /> Cetak
                    </button>
                    <div className="h-px bg-ink/10 my-1"></div>
                    <button
                      className="inline-flex items-center gap-3 text-sm font-bold text-coral hover:bg-coral/10 px-4 py-3 text-left"
                      onClick={() => { setShowDropdown(false); clearCompare(); }}
                    >
                      <Trash2 size={16} /> Kosongkan Semua
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {selected.length === 0 ? (
        <div className="mt-10 bg-warm ink-border rounded-card p-12 text-center max-w-3xl mx-auto">
          <AlertCircle size={48} className="mx-auto mb-4 text-ink/20" />
          <p className="text-2xl font-extrabold mb-2">Belum ada sekolah untuk dibandingkan</p>
          <p className="text-ink/60 mb-8 text-lg">
            Tambahkan sekolah dari halaman jelajah atau dari hasil School Match Anda.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button to="/schools" size="lg">
              Jelajahi Sekolah
            </Button>
            <Button to="/match" variant="secondary" size="lg">
              Coba AI School Match
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Picker UI when adding a new school */}
          {picking && count < max && (
            <div className="bg-cream-2 ink-border rounded-card p-6 animate-fade-down" data-testid="compare-picker">
              <div className="flex justify-between items-center mb-4">
                <p className="font-extrabold text-lg">Pilih sekolah untuk ditambahkan</p>
                <button onClick={() => setPicking(false)} className="p-1 hover:bg-ink/10 rounded-md">
                  <X size={20} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {available.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => {
                      toggleCompare(s.slug);
                      setPicking(false);
                    }}
                    data-testid={`compare-pick-${s.slug}`}
                    className="rounded-pill ink-border bg-warm px-4 py-2 text-sm font-semibold hover:bg-mint hover:text-ink transition-colors"
                  >
                    {s.name}
                  </button>
                ))}
                {available.length === 0 && (
                  <p className="text-ink/60 text-sm py-2">Semua sekolah di database sudah ditambahkan.</p>
                )}
              </div>
            </div>
          )}

          {/* Comparison Table wrapper for horizontal scroll */}
          <div className="bg-warm ink-border rounded-card overflow-hidden print:border-none print:shadow-none print:bg-transparent">
            <div className="overflow-x-auto no-scrollbar relative w-full print:overflow-visible print:w-auto">
              <table className="w-full text-left border-separate border-spacing-0 min-w-[800px] print:min-w-0 print:table-fixed">
                <thead>
                  <tr>
                    {/* Sticky Label Column Header */}
                    <th className="sticky left-0 z-30 bg-warm min-w-[120px] sm:min-w-[200px] w-[120px] sm:w-[200px] print:min-w-[80px] print:w-auto p-4 sm:p-6 print:p-2 border-b-2 border-r border-ink/30 align-bottom shadow-[4px_0_12px_rgba(33,26,77,0.05)] print:shadow-none print:bg-transparent">
                      <div className="font-extrabold text-sm sm:text-xl text-ink leading-tight">Aspek<br className="hidden sm:block"/> Perbandingan</div>
                    </th>

                    {/* School Columns Headers */}
                    {selected.map((school) => (
                      <th
                        key={school.id}
                        className="min-w-[230px] sm:min-w-[280px] w-[230px] sm:w-[300px] print:min-w-0 print:w-auto p-4 sm:p-6 print:p-2 border-b-2 border-r border-ink/10 align-top relative bg-warm print:bg-transparent group z-10"
                      >
                        <button
                          onClick={() => removeCompare(school.slug)}
                          className="absolute top-4 right-4 p-1.5 bg-ink/5 hover:bg-coral hover:text-warm text-ink rounded-full transition-colors z-10 print:hidden"
                          aria-label={`Hapus ${school.name} dari perbandingan`}
                        >
                          <X size={18} />
                        </button>

                        <Link to={`/schools/${school.slug}`} className="block mt-2">
                          <div className="aspect-[16/10] bg-ink/10 rounded-xl overflow-hidden mb-3 sm:mb-4 ink-border print:hidden">
                            <img
                              onError={(e) => { e.target.style.display = "none"; }}
                              src={school.images?.[0] || school.image}
                              alt={school.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <h3 className="text-lg sm:text-xl print:text-sm font-extrabold leading-tight hover:text-purple transition-colors line-clamp-2 print:line-clamp-none">
                            {school.name}
                          </h3>
                        </Link>
                        <p className="text-ink/60 font-medium text-sm print:text-[10px] mt-1">{school.city}</p>
                      </th>
                    ))}

                    {/* Add School Column Header */}
                    {count < max && (
                      <th className="min-w-[230px] sm:min-w-[280px] w-[230px] sm:w-[300px] p-4 sm:p-6 border-b-2 border-ink/20 align-middle bg-cream print:hidden">
                        <button
                          onClick={() => setPicking((p) => !p)}
                          data-testid="compare-add"
                          className="w-full h-[180px] rounded-2xl border-2 border-dashed border-ink/40 flex flex-col items-center justify-center gap-3 text-ink/60 hover:border-ink hover:text-ink hover:bg-cream-2 transition-colors"
                        >
                          <Plus size={32} />
                          <span className="font-bold text-lg">Tambah Sekolah</span>
                          <span className="text-xs font-semibold">({count}/{max})</span>
                        </button>
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {CATEGORIES.map((cat) => (
                    <React.Fragment key={cat.title}>
                      {/* Category Header Row */}
                      <tr>
                        <td
                          colSpan={selected.length + (count < max ? 2 : 1)}
                          className="bg-cream/50 print:bg-transparent border-b-2 border-ink/10 px-4 sm:px-6 py-2 sm:py-3 print:py-1 print:px-2"
                        >
                          <h4 className="font-extrabold text-sm uppercase tracking-widest text-ink/50 sticky left-6 inline-block">
                            {cat.title}
                          </h4>
                        </td>
                      </tr>

                      {/* Data Rows */}
                      {cat.rows.map((row) => (
                        <tr key={row.label} className="group hover:bg-cream/50 transition-colors">
                          {/* Sticky Label Column */}
                          <td className="sticky left-0 z-20 bg-warm group-hover:bg-cream-2 print:bg-transparent p-4 sm:p-6 print:p-2 border-b border-r border-ink/30 font-bold text-ink/80 text-xs sm:text-base print:text-xs shadow-[4px_0_12px_rgba(33,26,77,0.05)] print:shadow-none align-top leading-snug">
                            {row.label}
                          </td>

                          {/* Data Columns */}
                          {selected.map((school) => (
                            <td
                              key={`${school.id}-${row.label}`}
                              className="p-4 sm:p-6 print:p-2 border-b border-r border-ink/10 text-ink/90 align-top leading-relaxed text-sm sm:text-base print:text-[10px] break-words"
                            >
                              {row.render(school)}
                            </td>
                          ))}

                          {/* Empty Cell for Add Column */}
                          {count < max && (
                            <td className="p-4 sm:p-6 border-b border-ink/10 bg-cream/30 print:hidden"></td>
                          )}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Print-only Footer */}
      <div className="hidden print:block mt-12 pt-6 border-t-2 border-ink/20 text-center text-sm font-semibold text-ink/60">
        &copy; {new Date().getFullYear()} MySchoolMate. Dicetak pada {new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}. Hak cipta dilindungi.
      </div>
    </div>
  );
}
